import React, { useState } from "react"
import { Box, Typography, Card, CardContent, Grid, Alert, Button, LinearProgress } from "@mui/material"
import { TrendingUp, TrendingDown, CheckCircle, MonetizationOn } from "@mui/icons-material"
import type { AuditProduct } from "../../types"
import AuditSummaryTable from "./AuditSummaryTable"
import { useGetAuditProductSummaryQuery } from "src/graphql/quark/quark.type"
import SummaryCardsSkeletons from "./SummaryCardsSkeletons"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import ConfirmationDialog from "src/shared/container/confirmation-dialog"

interface AuditSummaryProps {
  product: AuditProduct | null
  onSubmit: () => void
  isLoading: boolean
  onStepBack: () => void
}

const AuditSummary: React.FC<AuditSummaryProps> = ({ onSubmit, product, onStepBack, isLoading }) => {
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] = useState(false)
  const toggleDialog = () => setIsOpenConfirmationDialog(!isOpenConfirmationDialog)
  const { data, loading } = useGetAuditProductSummaryQuery({
    fetchPolicy: "network-only",
    variables: {
      auditProductId: product?.id || ""
    }
  })

  const summary = data?.getAuditProductSummary

  // Show skeleton loading state
  if (loading || !summary) {
    return <SummaryCardsSkeletons />
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Audit Summary - {product?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review the audit results before submitting
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <TrendingUp color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6">{summary.remainingLots}</Typography>
              <Typography variant="body2" color="text.secondary">
                Remaining Lots
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <TrendingDown color="secondary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6">{summary.scannedLots}</Typography>
              <Typography variant="body2" color="text.secondary">
                Scanned Lots
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <MonetizationOn color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6">{getDisplayPrice(summary.excessAmount ?? 0, "PKR ")}</Typography>
              <Typography variant="body2" color="text.secondary">
                Excess Amount
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <CheckCircle color={(summary?.shortAmount ?? 0) > 0 ? "error" : "success"} sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6">{getDisplayPrice(summary?.shortAmount ?? 0, "PKR ")}</Typography>
              <Typography variant="body2" color="text.secondary">
                Short Amount
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Discrepancy Alerts */}
      {((summary?.excessCount ?? 0) > 0 || (summary?.shortCount ?? 0) > 0) && (
        <Box sx={{ mb: 3 }}>
          {(summary?.shortCount ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>Short Amount:</strong> {summary.shortCount} stock value are missing from inventory
              </Typography>
            </Alert>
          )}
          {(summary?.excessCount ?? 0) > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>Excess Amount:</strong> {summary.excessCount} stock value found that are not in the system
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* Lots Detail Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Lot Details ({summary.totalLots} lots)
          </Typography>

          <AuditSummaryTable productId={product?.id || ""} />
        </CardContent>
      </Card>

      {/* Progress Indicator */}
      {isLoading && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Submitting audit results...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={onStepBack} disabled={isLoading} size="large">
          Back
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={isLoading} size="large">
          {isLoading ? "Submitting..." : "Submit Audit"}
        </Button>
      </Box>
      <ConfirmationDialog
        open={isOpenConfirmationDialog}
        toggleDialog={toggleDialog}
        loading={isLoading}
        onConfirm={onSubmit}
        closeOnConfirm={true}
        subtitle={`You want to submit ${product?.name} audit?`}
      />
    </Box>
  )
}

export default AuditSummary
